import React, {useMemo, useState} from 'react';
import {Provider as ReduxProvider} from 'react-redux';
import {BrowserRouter as Router} from 'react-router-dom';
import ReduxStore from "./redux";
import ApplicationViews from "./views";
import {CssBaseline} from "@mui/material";
import theme from "./theme";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {Experimental_CssVarsProvider as CssVarsProvider, StyledEngineProvider} from "@mui/material/styles";
import Api from "../core/services/api/api";
import {DynamicColumnApplications, DynamicColumnsProvider} from "./hooks/use-dynamic-columns";
import EnvService from "../core/services/env";
import ApplicationToastContainer from "./components/app-specific/toast-container";
import {NotebookConfigProvider} from "./hooks/use-notebook-config";
import DateUtils from "../core/services/date-utils";
import ApplicationDialogs from "./dialogs";
import {BizFormApiResolverProvider} from "./hooks/use-biz-form-api-resolver";
import {BizFileUrlsResolverProvider} from "./hooks/use-biz-file-urls-resolver";
import BizProductionApiService from "../core/services/api_service/production_api_service";
import {SavedComponentsStateProvider} from "./hooks/use-saved-components-state";
import {HelmetProvider} from "react-helmet-async";
import SEO from "./components/seo";
import {LoadingView} from "./views/loading";
import PartnerInformationProvider from "./hooks/use-partner-information";
import OperatorInformationProvider from "./hooks/use-operator-information";
import {AuthenticationProvider} from "./hooks/use-auth-state";
import {AuthBlockerDialog} from "./components/dialogs/auth-blocker-dialog";

const Application = () => {
    const [partnerInfo, setPartnerInfo] = useState(null);
    const [operatorInfo, setOperatorInfo] = useState(null);
    const currentColorTheme = useMemo(() => {
        try {
            return JSON.parse(partnerInfo?.colorThemeJson ?? '{}');
        } catch {
            return {}
        }
    }, [partnerInfo?.colorThemeJson]);

    const generatedTheme = useMemo(() => theme(currentColorTheme), [currentColorTheme]);

    const savedComponentsStateProviderCallbacks = useMemo(() => ({
        getComponentsSavedState: async (request, cancelToken) => (await Api.listUIComponents(request.applicationName, cancelToken))?.data,
        saveComponentState: async (state, cancelToken) => (await Api.saveUIComponent(state, cancelToken))?.resultFlag ?? false,
    }), [])

    return (
        // Suspense is here specifically for the internationalization loading.
        <React.Suspense fallback={<LoadingView message={"loading..."}/>}>
            <HelmetProvider>
                <CssVarsProvider defaultMode={'system'} theme={generatedTheme}>
                    <CssBaseline enableColorScheme/>
                    <StyledEngineProvider injectFirst>
                        <ReduxProvider store={ReduxStore}>
                            <BizFormApiResolverProvider>
                                <LocalizationProvider dateAdapter={DateUtils.adapter} dateLibInstance={DateUtils.instance}>
                                    <AuthenticationProvider>
                                        <BizFileUrlsResolverProvider>
                                            <PartnerInformationProvider setLocalState={setPartnerInfo}>
                                            <OperatorInformationProvider setLocalState={setOperatorInfo}>
                                                <SavedComponentsStateProvider
                                                    operatorId={operatorInfo?.id}
                                                    applicationName={EnvService.uiComponentsApplicationName}
                                                    {...savedComponentsStateProviderCallbacks}
                                                >
                                                    <DynamicColumnsProvider
                                                        fetchColumnsMap={{
                                                            [DynamicColumnApplications.default]: Api.fetchDynamicColumns.bind(Api),
                                                            [DynamicColumnApplications.BizNoteService]: Api.fetchNotesDynamicColumns.bind(Api),
                                                            [DynamicColumnApplications.BizTaskService]: Api.fetchTasksDynamicColumns.bind(Api),
                                                            [DynamicColumnApplications.BizNcr]: Api.fetchNcrDynamicColumns.bind(Api),
                                                            [DynamicColumnApplications.BizProduction]: BizProductionApiService.v1.reportingTools.fetchDynamicColumns.bind(BizProductionApiService),
                                                        }}
                                                        getFilterOptionsMap={{
                                                            [DynamicColumnApplications.default]: EnvService.ApiUrl + "CleanAF/ReportingTools",
                                                            [DynamicColumnApplications.BizNoteService]: EnvService.NotesApiUrl + "ReportingTools",
                                                            [DynamicColumnApplications.BizTaskService]: EnvService.TasksApiUrl + "ReportingTools",
                                                            [DynamicColumnApplications.BizNcr]: EnvService.NcrApiUrl + "ReportingTools",
                                                            [DynamicColumnApplications.BizProduction]: EnvService.BizProductionApiUrl + "/api/ReportingTools",
                                                        }}
                                                    >
                                                        <Router basename={EnvService.PublicUrl}>
                                                            <SEO/>
                                                            <NotebookConfigProvider>
                                                                <ApplicationViews/>
                                                                <ApplicationToastContainer/>
                                                                <ApplicationDialogs/>
                                                                {
                                                                    EnvService.DEV_useAuthBlocker &&
                                                                    <AuthBlockerDialog/>
                                                                }
                                                            </NotebookConfigProvider>
                                                        </Router>
                                                    </DynamicColumnsProvider>
                                                </SavedComponentsStateProvider>
                                            </OperatorInformationProvider>
                                        </PartnerInformationProvider>
                                        </BizFileUrlsResolverProvider>
                                    </AuthenticationProvider>
                                </LocalizationProvider>
                            </BizFormApiResolverProvider>
                        </ReduxProvider>
                    </StyledEngineProvider>
                </CssVarsProvider>
            </HelmetProvider>
        </React.Suspense>
    );
}


export default Application;
